import React from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, XCircle } from 'lucide-react';
import { FacilityCondition, facilityConditionColors } from '@/constant';

interface FacilityConditionIconProps {
  condition: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const FacilityConditionIcon: React.FC<FacilityConditionIconProps> = ({
  condition,
  size = 'md',
  showLabel = false,
  className = '',
}) => {
  const getIcon = () => {
    const iconSize =
      size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';

    switch (condition) {
      case FacilityCondition.EXCELLENT:
        return (
          <CheckCircle
            className={`${iconSize} text-green-700 dark:text-green-300 contrast:text-green-300`}
          />
        );
      case FacilityCondition.GOOD:
        return (
          <CheckCircle
            className={`${iconSize} text-blue-600 dark:text-blue-300 contrast:text-blue-300`}
          />
        );
      case FacilityCondition.NEEDS_MAINTENANCE:
        return (
          <AlertTriangle
            className={`${iconSize} text-yellow-700 dark:text-yellow-300 contrast:text-yellow-300`}
          />
        );
      case FacilityCondition.OUT_OF_ORDER:
        return (
          <XCircle
            className={`${iconSize} text-red-600 dark:text-red-300 contrast:text-red-300`}
          />
        );
      default:
        return <AlertCircle className={`${iconSize} text-gray-400`} />;
    }
  };

  const getLabel = () => {
    switch (condition) {
      case FacilityCondition.EXCELLENT:
        return 'Excellent';
      case FacilityCondition.GOOD:
        return 'Good';
      case FacilityCondition.NEEDS_MAINTENANCE:
        return 'Needs Maintenance';
      case FacilityCondition.OUT_OF_ORDER:
        return 'Out of Order';
      default:
        return 'Unknown';
    }
  };

  const getBadgeClasses = () => {
    const baseClasses =
      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';
    const conditionClasses =
      facilityConditionColors[condition as FacilityCondition] ||
      'bg-gray-100 text-gray-800 border-gray-200';
    return `${baseClasses} ${conditionClasses}`;
  };

  if (showLabel) {
    return (
      <div className={`${getBadgeClasses()} ${className}`}>
        {getIcon()}
        <span className="ml-1.5">{getLabel()}</span>
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center ${className}`} title={getLabel()}>
      {getIcon()}
    </div>
  );
};

export default FacilityConditionIcon;
